import React from 'react';
import PropTypes from 'prop-types';
import { LuChevronDown, LuDownload, LuPackage, LuRefreshCw, LuRocket } from 'react-icons/lu';
import { ActionDropdown, Button } from 'components/common';

// Toolbar actions of the 3-Months Production Forecast page:
//   [Release to Production]  [Actions ▾ → Export · Request materials · Recalc from documents]
// The primary Release button leads; every secondary action lives in one
// dropdown so the bar stays a single tidy row.

const TRIGGER_CLASS = [
  'tw-inline-flex tw-items-center tw-gap-2 tw-h-9 tw-px-3.5',
  'tw-text-[13px] tw-font-semibold tw-whitespace-nowrap tw-select-none',
  'tw-bg-white tw-text-slate-700 tw-border tw-border-solid tw-border-slate-200 tw-rounded-lg',
  'hover:tw-bg-slate-50 tw-cursor-pointer tw-transition-colors',
  'focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-primary-300',
].join(' ');

const SELECTION_HINT = 'Select rows in the grid first';

const withCount = (label, count) => (count > 0 ? `${label} (${count})` : label);

// Selection actions explain why they are unavailable right in the menu.
const selectionLabel = (label, count) => (count > 0 ? withCount(label, count) : (
  <span className="tw-flex tw-flex-col tw-leading-tight">
    <span>{label}</span>
    <span className="tw-text-[10px] tw-font-normal tw-text-slate-400">{SELECTION_HINT}</span>
  </span>
));

const PlanningToolbarActions = ({
  loading,
  exporting,
  onExport,
  selectedCount,
  onRequestMaterials,
  onRecalculateDocuments,
  unsentCount,
  onReleaseToProduction,
}) => {
  const noSelection = selectedCount === 0;

  const items = [
    {
      label: exporting ? 'Exporting…' : 'Export to Excel',
      icon: <LuDownload size={14} />,
      onClick: onExport,
      disabled: loading || exporting,
    },
    { divider: true },
    {
      label: selectionLabel('Request materials', selectedCount),
      icon: <LuPackage size={14} />,
      onClick: onRequestMaterials,
      disabled: noSelection,
    },
    {
      label: selectionLabel('Recalc from documents', selectedCount),
      icon: <LuRefreshCw size={14} />,
      onClick: onRecalculateDocuments,
      disabled: noSelection,
    },
  ];

  return (
    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1">
      <Button
        variant="primary"
        leftIcon={LuRocket}
        onClick={onReleaseToProduction}
        disabled={loading || unsentCount === 0}
        title={unsentCount === 0 ? 'All records released' : 'Release unsent records to Production'}
      >
        {withCount('Release to Production', unsentCount)}
      </Button>

      <ActionDropdown
        unstyledTrigger
        triggerClassName={TRIGGER_CLASS}
        menuWidth={240}
        items={items}
        trigger={(
          <>
            Actions
            {!noSelection && (
              <span
                title={`${selectedCount} selected`}
                className="tw-inline-flex tw-items-center tw-justify-center tw-min-w-[18px] tw-h-[18px] tw-px-1 tw-rounded-xl tw-bg-primary-100 tw-text-primary-700 tw-text-[11px] tw-font-bold"
              >
                {selectedCount}
              </span>
            )}
            <LuChevronDown size={14} className="tw-text-slate-400" aria-hidden="true" />
          </>
        )}
      />
    </div>
  );
};

PlanningToolbarActions.propTypes = {
  loading: PropTypes.bool,
  exporting: PropTypes.bool,
  onExport: PropTypes.func.isRequired,
  selectedCount: PropTypes.number.isRequired,
  onRequestMaterials: PropTypes.func.isRequired,
  onRecalculateDocuments: PropTypes.func.isRequired,
  unsentCount: PropTypes.number.isRequired,
  onReleaseToProduction: PropTypes.func.isRequired,
};

export default PlanningToolbarActions;
